export type RequisitionStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'ON_HOLD' | 'FILLED' | 'CANCELLED'

export type ApplicationStatus =
  | 'IN_PROGRESS' | 'OFFERED' | 'HIRED' | 'REJECTED' | 'WITHDRAWN'

export type OfferStatus =
  | 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'SENT' | 'ACCEPTED' | 'DECLINED'
  | 'WITHDRAWN' | 'EXPIRED'

export type CandidateStatus = 'ACTIVE' | 'HIRED' | 'WITHDRAWN' | 'BLACKLISTED'

export type StageKind =
  | 'APPLIED' | 'SCREENING' | 'INTERVIEW' | 'ASSESSMENT' | 'OFFER' | 'HIRED' | 'REJECTED'

export type Recommendation = 'STRONG_YES' | 'YES' | 'NEUTRAL' | 'NO' | 'STRONG_NO'

export const REQUISITION_STATUS_LABELS: Record<RequisitionStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Open for applications',
  REJECTED: 'Rejected',
  ON_HOLD: 'On hold',
  FILLED: 'Filled',
  CANCELLED: 'Cancelled',
}

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  IN_PROGRESS: 'In progress',
  OFFERED: 'Offered',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrew',
}

export const OFFER_STATUS_LABELS: Record<OfferStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Awaiting approval',
  APPROVED: 'Approved, not sent',
  SENT: 'Sent, awaiting a reply',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  WITHDRAWN: 'Withdrawn',
  EXPIRED: 'Expired',
}

export const STAGE_KIND_LABELS: Record<StageKind, string> = {
  APPLIED: 'Applied',
  SCREENING: 'Screening',
  INTERVIEW: 'Interview',
  ASSESSMENT: 'Assessment',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
}

/**
 * Words, not numbers, and the API agrees.
 *
 * A five-point numeric scale invites averaging, and averaging four opinions into 3.25 hides
 * the one strong objection — usually the most informative thing the panel said.
 */
export const RECOMMENDATION_LABELS: Record<Recommendation, string> = {
  STRONG_YES: 'Strong yes',
  YES: 'Yes',
  NEUTRAL: 'Could go either way',
  NO: 'No',
  STRONG_NO: 'Strong no',
}

export interface PipelineStageView {
  id: string
  requisitionId: string
  stageOrder: number
  name: string
  stageKind: string
  requiresInterview: boolean
  terminal: boolean
}

export interface JobRequisitionView {
  id: string
  legalEntityId: string
  establishmentId: string | null
  departmentId: string
  departmentName: string | null
  designationId: string | null
  gradeId: string | null
  locationId: string | null
  positionId: string | null
  reference: string
  title: string
  description: string | null
  headcount: number
  employmentType: string | null
  contractType: string | null
  salaryRangeMin: number | null
  salaryRangeMax: number | null
  currency: string | null
  fiscalYear: number | null
  targetStartDate: string | null
  justification: string | null
  /** False when no budget covered this department and year — not the same as within budget. */
  budgetChecked: boolean
  budgetApprovedHeads: number | null
  budgetFilledHeads: number | null
  budgetOverage: number | null
  budgetNote: string | null
  status: string
  raisedBy: string | null
  submittedAt: string | null
  decidedAt: string | null
  decidedBy: string | null
  decisionComment: string | null
  stages: PipelineStageView[]
  applicationCount: number
}

export interface CandidateView {
  id: string
  legalEntityId: string | null
  firstName: string
  middleName: string | null
  lastName: string | null
  displayName: string
  email: string | null
  phone: string | null
  nationality: string | null
  dateOfBirth: string | null
  currentEmployer: string | null
  currentTitle: string | null
  totalExperienceYears: number | null
  noticePeriodDays: number | null
  expectedSalary: number | null
  expectedCurrency: string | null
  cvFileId: string | null
  sourceId: string | null
  referredByEmployeeId: string | null
  hiredEmployeeId: string | null
  hiredAt: string | null
  status: string
  notes: string | null
  customFields: Record<string, unknown>
  applicationCount: number
}

export interface JobApplicationView {
  id: string
  candidateId: string
  candidateName: string | null
  requisitionId: string
  requisitionTitle: string | null
  postingId: string | null
  stageId: string | null
  stageName: string | null
  stageKind: string | null
  reference: string
  appliedOn: string
  sourceId: string | null
  status: string
  rejectionReasonId: string | null
  rejectionReasonName: string | null
  rejectedAt: string | null
  rejectionComment: string | null
  interviewCount: number
  /** "No interview yet" reads differently from "0 of 3 replied", and must. */
  feedbackProgress: string
}

export interface InterviewFeedbackView {
  id: string
  interviewId: string
  interviewerEmployeeId: string
  interviewerName: string | null
  submitted: boolean
  submittedAt: string | null
  recommendation: string | null
  strengths: string | null
  concerns: string | null
  scorecard: Record<string, unknown>
}

export interface InterviewView {
  id: string
  applicationId: string
  stageId: string | null
  roundNumber: number
  title: string | null
  mode: string
  scheduledStart: string | null
  scheduledEnd: string | null
  location: string | null
  meetingLink: string | null
  status: string
  outcome: string | null
  notes: string | null
  panel: InterviewFeedbackView[]
}

export interface OfferView {
  id: string
  applicationId: string
  candidateName: string | null
  reference: string
  basicSalary: number | null
  totalPackage: number | null
  currency: string
  salaryBreakdown: string | null
  joiningDate: string | null
  probationMonths: number | null
  noticePeriodDays: number | null
  gradeId: string | null
  designationId: string | null
  status: string
  preparedBy: string | null
  approvedAt: string | null
  approvedBy: string | null
  sentAt: string | null
  validUntil: string | null
  respondedAt: string | null
  declineReason: string | null
  letterFileId: string | null
  /** Null when the requisition had no range to check against. */
  withinSalaryRange: boolean | null
  rangeNote: string | null
}

export interface PipelineColumn {
  stageId: string
  stageName: string
  stageKind: string
  stageOrder: number
  applications: JobApplicationView[]
}

export interface RecruitmentBoardView {
  requisitionId: string
  requisitionTitle: string
  requisitionStatus: string
  columns: PipelineColumn[]
}

export interface RecruitmentSourceView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  paid: boolean
  active: boolean
}

export interface RejectionReasonView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  appliesAtStage: string | null
  reconsider: boolean
  active: boolean
}

export interface JobPostingView {
  id: string
  requisitionId: string
  title: string
  summary: string | null
  visibility: string
  sourceId: string | null
  cost: number | null
  currency: string | null
  postedOn: string | null
  closesOn: string | null
  externalReference: string | null
  status: string
}

export interface RecruitmentSettingView {
  id: string
  legalEntityId: string
  onboardingBlockedByProCase: boolean
  requireAcceptedOfferToHire: boolean
  defaultOfferValidityDays: number | null
  notes: string | null
}

export interface HireReadinessView {
  ready: boolean
  blockers: string[]
  acceptedOfferId: string | null
  joiningDate: string | null
}

export interface HireResultView {
  employeeId: string
  employeeCode: string | null
  displayName: string | null
  joinDate: string | null
  candidateId: string
}

export interface RecruiterDashboardView {
  openRequisitions: number
  overBudgetRequisitions: number
  activeCandidates: number
  interviewsScheduled: number
  offersOutstanding: number
  hiresThisMonth: number
  /** Set when nothing is configured, so the zeros are explained rather than left bare. */
  emptyReason: string | null
}

/** What an approver sees on a requisition. */
export interface RequisitionDecisionContext {
  requisition: JobRequisitionView
  otherOpenInDepartment: JobRequisitionView[]
  budgetNote: string | null
  salaryRangeNote: string | null
}

/** What an approver sees on an offer. */
export interface OfferDecisionContext {
  offer: OfferView
  requisition: JobRequisitionView
  candidate: CandidateView
  rangeNote: string | null
  validityNote: string | null
}
